import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { calculateVat, formatCurrency, RECURRENCE_OPTIONS, toDate, advanceDate } from '../utils/invoiceUtils';

export default function EditInvoice({ userRole }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([{ description: '', quantity: 1, price: 0 }]);
  const [clients, setClients] = useState([]);
  const [nextRunDate, setNextRunDate] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    customerName: '',
    customerAddress: '',
    customerEmail: '',
    customerPhone: '',
    invoiceDate: '',
    dueDate: '',
    invoiceNo: '',
    status: 'pending',
    notes: '',
    vatRate: 0,
    taxInclusive: false,
    taxRegNo: '',
    recurrenceFrequency: 'none'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        let snapshot;
        if (userRole === 'admin') {
          snapshot = await getDocs(query(collection(db, 'clients'), orderBy('name', 'asc')));
        } else {
          snapshot = await getDocs(query(collection(db, 'clients'), where('userId', '==', auth.currentUser.uid)));
        }
        const clientList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (userRole !== 'admin') {
          clientList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }
        setClients(clientList);
      } catch (fetchError) {
        console.error('Error fetching clients:', fetchError);
      }
    };

    fetchClients();
  }, [userRole]);

  const handleClientSelect = (e) => {
    const clientId = e.target.value;
    if (!clientId) return;
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    setFormData({
      ...formData,
      customerName: client.name || '',
      customerEmail: client.email || '',
      customerPhone: client.phone || '',
      customerAddress: client.address || ''
    });
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const invoiceRef = doc(db, 'invoices', id);
        const invoiceSnap = await getDoc(invoiceRef);
        
        if (invoiceSnap.exists()) {
          const invoiceData = invoiceSnap.data();
          
          setFormData({
            companyName: invoiceData.companyName || '',
            companyEmail: invoiceData.companyEmail || '',
            companyPhone: invoiceData.companyPhone || '',
            companyAddress: invoiceData.companyAddress || '',
            // Use customer naming convention consistently
            customerName: invoiceData.customerName || invoiceData.clientName || '',
            customerAddress: invoiceData.customerAddress || invoiceData.clientAddress || '',
            customerEmail: invoiceData.customerEmail || invoiceData.clientEmail || '',
            customerPhone: invoiceData.customerPhone || invoiceData.clientPhone || '',
            invoiceDate: invoiceData.invoiceDate || '',
            dueDate: invoiceData.dueDate ? (invoiceData.dueDate.toDate ? invoiceData.dueDate.toDate().toISOString().split('T')[0] : invoiceData.dueDate) : '',
            invoiceNo: invoiceData.invoiceNo || invoiceData.invoiceNumber || '',
            status: invoiceData.status || 'pending',
            notes: invoiceData.notes || '',
            vatRate: invoiceData.vatRate || 0,
            taxInclusive: !!invoiceData.taxInclusive,
            taxRegNo: invoiceData.taxRegNo || '',
            recurrenceFrequency: invoiceData.recurrenceFrequency || 'none'
          });

          setNextRunDate(toDate(invoiceData.nextRunDate) || null);
          
          // Set items if they exist in the invoice data
          if (invoiceData.items && Array.isArray(invoiceData.items)) {
            setItems(invoiceData.items);
          }
        } else {
          setError('Invoice not found');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setError('Error loading invoice');
        setLoading(false);
      }
    };
    
    fetchInvoice();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update both naming conventions for customer/client fields
    if (name === 'customerName') {
      setFormData({
        ...formData,
        customerName: value,
        clientName: value
      });
    } else if (name === 'customerAddress') {
      setFormData({
        ...formData,
        customerAddress: value,
        clientAddress: value
      });
    } else if (name === 'customerEmail') {
      setFormData({
        ...formData,
        customerEmail: value,
        clientEmail: value
      });
    } else if (name === 'clientName') {
      setFormData({
        ...formData,
        clientName: value,
        customerName: value
      });
    } else if (name === 'clientEmail') {
      setFormData({
        ...formData,
        clientEmail: value,
        customerEmail: value
      });
    } else if (name === 'clientAddress') {
      setFormData({
        ...formData,
        clientAddress: value,
        customerAddress: value
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, price: 0 }]);
  };

  const removeItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const calculateTotals = () => calculateVat(items, formData.vatRate, formData.taxInclusive);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const invoiceRef = doc(db, 'invoices', id);
      
      // Calculate VAT-related values
      const totals = calculateTotals();
      
      // Create an object with both naming conventions
      const updateData = {
        ...formData,
        items,
        subtotal: totals.subtotal,
        vatAmount: totals.vatAmount,
        vatRate: Number(formData.vatRate),
        taxInclusive: formData.taxInclusive,
        taxRegNo: formData.taxRegNo,
        total: totals.total,
        nextRunDate: formData.recurrenceFrequency !== 'none'
          ? (nextRunDate || advanceDate(formData.dueDate ? new Date(formData.dueDate) : new Date(), formData.recurrenceFrequency))
          : null,
        // Ensure both naming conventions are included
        customerName: formData.customerName,
        customerAddress: formData.customerAddress,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        clientName: formData.customerName,
        clientEmail: formData.customerEmail,
        clientAddress: formData.customerAddress,
        clientPhone: formData.customerPhone,
        // Ensure invoice number is saved with both conventions
        invoiceNo: formData.invoiceNo,
        invoiceNumber: formData.invoiceNo
      };
      
      await updateDoc(invoiceRef, updateData);
      
      navigate(`/invoice/${id}`);
    } catch (error) {
      console.error('Error updating invoice:', error);
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner" role="status" aria-label="Loading" />
    </div>
  );
  
  if (error) return (
    <div className="invoice-container">
      <p className="error-message">{error}</p>
    </div>
  );

  return (
    <div className="form-container">
      <div className="form-section">
        <h1 className="form-title">Edit Invoice</h1>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="form-section">
            <h2 className="form-section-title">Company Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Company Email</label>
                <input
                  type="email"
                  name="companyEmail"
                  value={formData.companyEmail}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Company Phone</label>
                <input
                  type="text"
                  name="companyPhone"
                  value={formData.companyPhone}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group form-grid-full">
                <label className="form-label">Company Address</label>
                <textarea
                  name="companyAddress"
                  value={formData.companyAddress}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="2"
                ></textarea>
              </div>
              <div className="form-group">
                <label className="form-label">VAT</label>
                <select
                  name="vatRate"
                  value={formData.vatRate}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value={0}>Not VAT registered</option>
                  <option value={0.15}>VAT registered (15%)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">VAT Registration No.</label>
                <input
                  type="text"
                  name="taxRegNo"
                  value={formData.taxRegNo}
                  onChange={handleChange}
                  disabled={Number(formData.vatRate) === 0}
                  className="form-input"
                  placeholder="e.g. 4123456789"
                />
              </div>
              <div className="form-group form-grid-full">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    name="taxInclusive"
                    checked={formData.taxInclusive}
                    onChange={handleCheckboxChange}
                    disabled={Number(formData.vatRate) === 0}
                    className="form-checkbox-input"
                  />
                  <span>Prices already include VAT (tax-inclusive)</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Customer Information</h2>
            <div className="form-grid">
              {clients.length > 0 && (
                <div className="form-group form-grid-full">
                  <label className="form-label">Saved Client (auto-fill)</label>
                  <select
                    onChange={handleClientSelect}
                    defaultValue=""
                    className="form-select"
                  >
                    <option value="">Select a saved client…</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Customer Name</label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Customer Email</label>
                <input
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Customer Phone</label>
                <input
                  type="text"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group form-grid-full">
                <label className="form-label">Customer Address</label>
                <textarea
                  name="customerAddress"
                  value={formData.customerAddress}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="2"
                ></textarea>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Invoice Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Invoice Number</label>
                <input
                  type="text"
                  name="invoiceNo"
                  value={formData.invoiceNo}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Invoice Date</label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Repeat Invoice</label>
                <select
                  name="recurrenceFrequency"
                  value={formData.recurrenceFrequency}
                  onChange={handleChange}
                  className="form-select"
                >
                  {RECURRENCE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <div className="items-header">
              <h2 className="form-section-title mb-0">Invoice Items</h2>
              <button
                type="button"
                onClick={addItem}
                className="add-item-btn"
              >
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                  </svg>
                Add Item
              </button>
            </div>
            
            <div className="items-container">
              <div className="items-grid-header">
                <div>Description</div>
                <div className="text-center">Quantity</div>
                <div className="text-center">Price (ZAR)</div>
                <div className="text-right">Amount</div>
                <div></div>
              </div>
              
              {items.map((item, index) => (
                <div key={index} className="item-row">
                  <input
                    type="text"
                    placeholder="e.g. Website design service"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    required
                    className="item-input item-input--description"
                  />
                  <input
                    type="number"
                    placeholder="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                    min="1"
                    required
                    className="item-input item-input--quantity"
                  />
                  <input
                    type="number"
                    placeholder="0.00"
                    value={item.price}
                    onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    required
                    className="item-input item-input--price"
                  />
                  <span className="item-amount">
                    {formatCurrency((item.quantity || 0) * (item.price || 0))}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="item-remove-btn"
                    disabled={items.length === 1}
                    aria-label={`Remove item ${index + 1}`}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14ZM10 11v6M14 11v6" />
                    </svg>
                  </button>
                </div>
              ))}
              <div className="items-total">
                <div className="items-total-row">
                  <span>Subtotal{Number(formData.vatRate) > 0 && !formData.taxInclusive ? ' (excl. VAT)' : ''}</span>
                  <span>{formatCurrency(calculateTotals().subtotal)}</span>
                </div>
                
                {Number(formData.vatRate) > 0 && (
                  <div className="items-total-row">
                    <span>VAT ({(Number(formData.vatRate) * 100).toFixed(0)}%{formData.taxInclusive ? ', incl.' : ''})</span>
                    <span>{formatCurrency(calculateTotals().vatAmount)}</span>
                  </div>
                )}
                
                <div className="items-total-row items-total-row--grand">
                  <span>{Number(formData.vatRate) > 0 && formData.taxInclusive ? 'Total (incl. VAT)' : 'Total'}</span>
                  <span>{formatCurrency(calculateTotals().total)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Additional Notes</h2>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="form-textarea"
              rows="3"
            ></textarea>
          </div>
          
          <div className="form-footer">
            <button
              type="button"
              onClick={() => navigate(`/invoice/${id}`)}
              className="btn btn-secondary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
